/**
 * PushOptIn — settings card to enable browser/PWA push notifications.
 * Handles permission states (unsupported, denied, granted, default) and
 * lets the user send a test push to confirm wiring.
 */
import React, { useState } from "react";
import { Bell, BellOff, BellRing, AlertTriangle } from "lucide-react";
import usePushSubscription from "../lib/usePushSubscription";
import { toast } from "sonner";

export default function PushOptIn() {
  const { supported, status, permission, deviceCount, subscribe, unsubscribe, sendTest } = usePushSubscription();
  const [busy, setBusy] = useState(false);

  if (!supported) {
    return (
      <section data-testid="push-opt-in" className="hairline p-6 mt-8">
        <div className="flex items-start gap-3">
          <AlertTriangle size={16} className="mt-1 text-muted-foreground" />
          <div>
            <div className="serif text-xl text-foreground">Push notifications</div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Your browser doesn&apos;t support push notifications. If you&apos;re on iOS, install Cor·relate
              to your Home Screen first, then revisit this setting from the installed app.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const isSubscribed = status === "subscribed";
  const isDenied = permission === "denied" || status === "denied";

  const onSubscribe = async () => {
    setBusy(true);
    const res = await subscribe();
    setBusy(false);
    if (res.ok) {
      toast.success("Notifications on — the archive will whisper.");
    } else if (res.reason === "denied") {
      toast.error("Permission denied. Re-enable from your browser site settings.");
    } else if (res.reason === "vapid_missing") {
      toast.error("Push isn't configured server-side yet.");
    } else if (res.reason !== "default") {
      toast.error("Could not subscribe.");
    }
  };

  const onUnsubscribe = async () => {
    setBusy(true);
    await unsubscribe();
    setBusy(false);
    toast.success("Notifications off on this device.");
  };

  const onTest = async () => {
    setBusy(true);
    const { sent } = await sendTest();
    setBusy(false);
    if (sent > 0) toast.success(`Test push sent to ${sent} device${sent !== 1 ? "s" : ""}.`);
    else toast.error("No active subscriptions to ping.");
  };

  return (
    <section data-testid="push-opt-in" className="hairline p-6 mt-8">
      <div className="flex items-start gap-3 mb-4">
        {isSubscribed ? (
          <BellRing size={16} className="mt-1 text-[hsl(var(--primary))]" />
        ) : (
          <Bell size={16} className="mt-1 text-muted-foreground" />
        )}
        <div className="flex-1">
          <div className="serif text-xl text-foreground">Push notifications</div>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Get a quiet ping on this device when someone replies, follows you, or shares a
            correlation. Works best when you&apos;ve installed Cor·relate to your Home Screen.
          </p>
          {deviceCount > 0 && (
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-3">
              {deviceCount} device{deviceCount !== 1 ? "s" : ""} subscribed across your account
            </p>
          )}
        </div>
      </div>

      {isDenied && (
        <div data-testid="push-denied-notice" className="mb-4 p-3 paper text-sm text-muted-foreground leading-relaxed">
          You blocked notifications for Cor·relate. Re-enable them in your browser&apos;s site settings,
          then come back here.
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {!isSubscribed ? (
          <button
            data-testid="push-subscribe"
            onClick={onSubscribe}
            disabled={busy || isDenied}
            className="btn-gold disabled:opacity-40"
          >
            {busy ? "Waiting…" : "Enable notifications"}
          </button>
        ) : (
          <>
            <button
              data-testid="push-unsubscribe"
              onClick={onUnsubscribe}
              disabled={busy}
              className="btn-ghost flex items-center gap-2"
            >
              <BellOff size={13} />
              Turn off on this device
            </button>
            <button
              data-testid="push-test"
              onClick={onTest}
              disabled={busy}
              className="reaction-pill"
            >
              Send test ping
            </button>
          </>
        )}
      </div>
    </section>
  );
}
